import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DotLottleLoader } from './Loader';
import { startFocusedSigning } from '../utils/docusignFocusedSigning';
import { apiErrorCode } from '../utils/apiError';

// Friendly text for the backend's typed error codes (control() delivers
// them as a bare JSON string, which apiErrorCode normalizes).
const SIGNING_ERROR_TEXT: Record<string, string> = {
  EnvelopeAlreadySigned: 'This Statement of Work has already been signed.',
  EnvelopeNotActive: 'This Statement of Work is no longer active.',
  ExpertHasNotSigned: "The expert hasn't signed yet — countersigning unlocks after their signature.",
  NoEnvelopeFound: 'No Statement of Work was found to sign.',
  CountersignStatusUnavailable:
    "Couldn't confirm the countersign status with DocuSign — try again in a moment.",
};

interface Props {
  open: boolean;
  /**
   * Backend endpoint returning { url, integrationKey, jsUrl } — the expert's
   * own signing-url path, or the countersign-url path for the expert team.
   */
  signingUrlPath: string | null;
  title?: string;
  onClose: () => void;
  /** Fired when DocuSign reports signing_complete for this session. */
  onSigned: () => void;
}

/**
 * Large modal with a Statement of Work embedded for signature (DocuSign
 * focused view — same pattern as the MSA signing in
 * pages/pro/createEditProfile/formFragments/documents.tsx). Serves both the
 * expert's own signature and the company countersignature; the caller picks
 * the signing-url endpoint.
 */
const ExpertSowSignModal: React.FC<Props> = ({
  open,
  signingUrlPath,
  title = 'Sign Your Statement of Work',
  onClose,
  onSigned,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const signingContainerRef = useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !signingUrlPath) return undefined;
    // The signing URL is single-use and expires in 5 minutes, so it is
    // fetched fresh on every open.
    let cancelled = false;

    const startSigning = async () => {
      try {
        setLoading(true);
        setError('');

        await startFocusedSigning({
          signingUrlPath,
          // Guarded like the callbacks below: on the expert page ONE modal
          // instance serves every card, so a stale in-flight session must
          // not mount into the container of a reopened modal for a
          // different envelope. Returning null makes the util throw, and
          // the catch below swallows it via the cancelled check.
          getContainer: () => (cancelled ? null : signingContainerRef.current),
          onReady: () => {
            if (!cancelled) setLoading(false);
          },
          onSessionEnd: (event) => {
            if (cancelled) return;
            if (event.sessionEndType === 'signing_complete') {
              onSigned();
            }
            onClose();
          },
        });
      } catch (err: any) {
        if (cancelled) return;
        console.error('Failed to start SOW signing:', err);
        const code = apiErrorCode(err);
        setError(
          (code && SIGNING_ERROR_TEXT[code]) || 'Failed to start signing session'
        );
        setLoading(false);
      }
    };

    startSigning();

    return () => {
      // A stale session's events must not fire onSigned/onClose after the
      // modal has moved on; the iframe itself unmounts with the container.
      cancelled = true;
      if (signingContainerRef.current) {
        signingContainerRef.current.innerHTML = '';
      }
    };
  }, [open, signingUrlPath]);

  return (
    <Dialog open={open} maxWidth="lg" fullWidth fullScreen={isMobile} onClose={onClose}>
      <DialogTitle className="border-b border-lightgray4 pb-4">
        <div className="flex justify-between items-center">
          <Typography variant="h5">{title}</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon className="text-[#727272]" />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent className="!py-4">
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {/* Fixed-height container (matches the proven MSA reference) — the
            DocuSign iframe must never mount into a collapsed box. The loader
            overlays it while the session boots. */}
        <div style={{ position: 'relative', width: '100%', height: error ? 0 : 800 }}>
          {loading && !error && (
            <div
              className="flex items-center justify-center"
              style={{ position: 'absolute', inset: 0, zIndex: 1 }}
            >
              <DotLottleLoader fill={false} />
            </div>
          )}
          <div ref={signingContainerRef} style={{ width: '100%', height: '100%' }} />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExpertSowSignModal;
